/** Official provider artwork; source URLs and permitted presentation live in LOGOS.md. */

import type { ImageSourcePropType } from "react-native";

/**
 * A provider's mark.
 *
 * `aspect` is the artwork's width ÷ height, so a row can lay every mark out on a shared height and
 * derive its width, which keeps stacked marks reading as the same size. The entries are square;
 * the field keeps a non-square lockup from rendering stretched.
 */
export type ProviderLogo = {
  /** A bundled PNG, `require`d so Metro fingerprints it into the bundle. */
  source: ImageSourcePropType;
  aspect: number;
};

/**
 * TCGplayer's mark: three fanned cards in yellow, blue and red with the white bolt on the front
 * card. Taken from the 512px icon their `site.webmanifest` names and downscaled to 256, because
 * `LOGOS.md` asks for under about 500px on the long edge; 256 is still 16× a row's draw height.
 */
const TCGPLAYER: ProviderLogo = {
  source: require("../../../assets/providers/tcgplayer.png"),
  aspect: 1,
};

/**
 * PriceCharting's chart glyph in blue and green: the icon half of their site header lockup, which
 * they publish as their touch icon. The full 200×62 lockup's wordmark is illegible at 16px, and the
 * row sets "PriceCharting" in text beside the glyph anyway.
 *
 * Kept at its published 192px and never upscaled to match TCGplayer's 256, because a blurry mark is
 * worse than a smaller source; 192 is still 12× a row's draw height.
 */
const PRICECHARTING: ProviderLogo = {
  source: require("../../../assets/providers/pricecharting.png"),
  aspect: 1,
};

/**
 * Provider marks keyed by `PriceStack`'s row key (the provider name a `SourceQuote`'s `source` folds
 * onto through `ROW_ALIASES`), not by the display label, so rewording a label cannot break the
 * lookup. `tcgplayer` is the provider; "TCGplayer" is what a dealer reads.
 *
 * Because of that fold, one entry covers `sku-mirror`, `tcgtracking` and
 * `tcgplayer-graded->scrydex`, the ingest paths that all name this company (TRO-813).
 */
export const PROVIDER_LOGOS: Readonly<Record<string, ProviderLogo>> = {
  tcgplayer: TCGPLAYER,
  misprint: { source: require("../../../assets/providers/misprint.png"), aspect: 1 },
  pricecharting: PRICECHARTING,
};

/**
 * Provider lockups, the mark with the provider's name, for a line that names the provider with its
 * artwork in place of its name: "from [TCGplayer]" on the Mkt Price row and "Find it on [eBay]".
 *
 * Keyed like `PROVIDER_LOGOS`, plus `ebay`, which is a destination rather than a price source: the
 * lockup names where the sold search opens, never a price eBay quoted. Both are wide, so a caller
 * passes the height it draws them at; see `LOGOS.md` for the sizes they stay legible at.
 */
export const PROVIDER_LOCKUPS: Readonly<Record<string, ProviderLogo>> = {
  fanatics: { source: require("../../../assets/providers/fanatics-lockup.png"), aspect: 480 / 68 },
  alt: { source: require("../../../assets/providers/alt.png"), aspect: 1 },
  tcgplayer: {
    source: require("../../../assets/providers/tcgplayer-lockup.png"),
    aspect: 480 / 181,
  },
  ebay: { source: require("../../../assets/providers/ebay.png"), aspect: 480 / 195 },
};
